import React from 'react';
import { lightTheme as T } from '../Constants/Colors';

// ChartTooltip — a lightweight floating tooltip for data-viz hover.
//
// It renders an absolutely-positioned card at (x, y) inside the NEAREST
// positioned ancestor, so wrap your chart in a `position: relative` container
// and feed it cursor/point coordinates measured relative to that container.
// Pointer events are disabled so it never steals hover from the chart beneath.
//
// Content: pass either `title` + `items` (a list of { label, value, color })
// for the common "legend swatch → value" layout, or arbitrary `children`.
//
// Props:
//   visible  — render only when true (kept mounted-free otherwise)
//   x, y     — px offset from the positioned container's top-left
//   title    — optional header line (e.g. the hovered date)
//   items    — [{ label, value, color }] rows
//   align    — 'left' | 'right', which side of (x, y) the card sits on so it
//              stays inside the chart near the edges (default 'left')
function ChartTooltip({ visible, x = 0, y = 0, title, items = [], align = 'left', children }) {
    if (!visible) return null;

    return (
        <div
            role="tooltip"
            style={{
                position: 'absolute',
                left: x,
                top: y,
                transform: `translate(${align === 'right' ? 'calc(-100% - 12px)' : '12px'}, -50%)`,
                pointerEvents: 'none',
                zIndex: 20,
                minWidth: 132,
                maxWidth: 240,
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusSm,
                boxShadow: T.shadow,
                padding: '10px 12px',
                fontSize: 12,
                lineHeight: 1.45,
                color: T.textSecondary,
            }}
        >
            {title != null && (
                <div
                    style={{
                        fontWeight: 600,
                        color: T.textPrimary,
                        marginBottom: items.length ? 6 : 0,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {title}
                </div>
            )}

            {items.map((item, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginTop: i ? 2 : 0,
                    }}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        {item.color && (
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: item.color,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.label}
                        </span>
                    </span>
                    <span style={{ fontWeight: 600, color: T.textPrimary, flexShrink: 0 }}>
                        {item.value}
                    </span>
                </div>
            ))}

            {children}
        </div>
    );
}

export default ChartTooltip;
